import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, ButtonBase, Stack, Typography} from 'views/components';
import {ChevronRight, KeyboardArrowDown, KeyboardArrowUp} from 'views/icons';
import {Translate, translate} from 'views/module';

import {
  formatCompactDuration,
  formatDateTime,
  formatJobTime,
  formatMetricDate,
  getActivityLabel,
  getEarliestJob,
  getFailureCategoryLabel,
  getFlagDetailLabel,
  getOpenForLabel,
  getParticipantList,
  getParticipantSummary,
  getStatusLabel,
  isJobOverdue,
} from './monitorUtils';
import {getActionLabel} from '../view/agent_jobs/agentJobTypes';
import backend from 'utils/backend';

const LOG_MESSAGE_MAX_CHARS = 600;

// The case RN column fits a full 44-character RN in 12px monospace.
export const rowGridColumns = '300px 72px minmax(124px, 1fr) minmax(216px, 2fr) 112px 120px 64px 28px';

const statusColours = {
  OPEN: '#1A1916',
  PENDING: '#1A1916',
  COMPLETE: '#008A00',
  DELETED: '#A8A49E',
};

const detailChipStyles = {
  OVERDUE_JOB: {color: '#B77900', backgroundColor: '#FFF7E5'},
  STUCK_OPEN: {color: '#B77900', backgroundColor: '#FFF7E5'},
  NOTHING_SCHEDULED: {color: '#B77900', backgroundColor: '#FFF7E5'},
  MARKED_BAD: {color: '#C4512C', backgroundColor: '#FFF1EC'},
  HUMAN_INTERVENTION: {color: '#4B4B55', backgroundColor: '#F1F1F4'},
  FLAGGED: {color: '#4B4B55', backgroundColor: '#F1F1F4'},
};

const mutedChipStyle = {color: '#4B4B55', backgroundColor: '#F1F1F4'};

const DetailChip = (props) => {
  const {
    chipStyle,
    children,
  } = props;

  return (
    <Box
      component='span'
      sx={{
        display: 'inline-block',
        fontSize: '11px',
        fontWeight: 500,
        lineHeight: '16px',
        px: 0.75,
        borderRadius: '4px',
        whiteSpace: 'nowrap',
        ...chipStyle,
      }}
    >
      {children}
    </Box>
  );
};

DetailChip.propTypes = {
  chipStyle: PropTypes.object.isRequired,
  children: PropTypes.node,
};

const lowerFirst = (text) => {
  return text ? text.charAt(0).toLowerCase() + text.slice(1) : text;
};

const formatNameList = (names) => {
  if (names.length <= 1) {
    return names[0] || '';
  }

  return `${names[0]} +${names.length - 1}`;
};

const gradeColours = {
  PASS: '#008A00',
  FAIL: '#C4512C',
  UNCERTAIN: '#B77900',
  SKIPPED_INTERVENTION: '#526071',
};

const getLastLogEntry = (caseDetail) => {
  const actionLog = Array.isArray(caseDetail?.actionLog) ? caseDetail.actionLog : [];

  return actionLog.reduce((latestEntry, entry) => {
    return !latestEntry || `${entry.createdAt}` > `${latestEntry.createdAt}` ? entry : latestEntry;
  }, null);
};

const truncateMessage = (message) => {
  const text = `${message || ''}`;

  return text.length > LOG_MESSAGE_MAX_CHARS ? `${text.slice(0, LOG_MESSAGE_MAX_CHARS)}…` : text;
};

const getGradeLabel = (grade) => {
  const gradeLabel = translate(`case.Detail.Marks.Grades.${grade}`);

  return gradeLabel === 'Missing translation' ? grade : gradeLabel;
};

const DetailSection = (props) => {
  const {
    label,
    children,
  } = props;

  return (
    <Box sx={{minWidth: 0}}>
      <Typography sx={{fontSize: '11px', color: '#A8A49E', mb: 0.5}}>
        {label}
      </Typography>
      {children}
    </Box>
  );
};

DetailSection.propTypes = {
  label: PropTypes.node.isRequired,
  children: PropTypes.node,
};

const NextJobDetail = (props) => {
  const {
    job,
    now,
  } = props;

  if (!job) {
    return (
      <Typography sx={{fontSize: '12px', color: '#A8A49E'}}>
        <Translate expression='case.Monitor.Expanded.NoNextJob' />
      </Typography>
    );
  }

  const isOverdue = isJobOverdue(job, now);
  const action = getActionLabel(job);
  const time = formatJobTime(job.executeAt, now);

  return (
    <Typography sx={{fontSize: '12px', fontWeight: isOverdue ? 600 : 400, color: isOverdue ? '#B77900' : '#1A1916'}}>
      {
        isOverdue ?
          translate('case.Monitor.Expanded.NextJobOverdue', {
            action,
            time,
            duration: formatCompactDuration(Math.max(0, now - new Date(job.executeAt).getTime())),
          }) :
          translate('case.Monitor.Expanded.NextJobAt', {action, time})
      }
    </Typography>
  );
};

NextJobDetail.propTypes = {
  job: PropTypes.object,
  now: PropTypes.number.isRequired,
};

const MarkDetail = (props) => {
  const {
    row,
    domainGuidesByRn,
  } = props;

  if (!row.markingGrade) {
    return (
      <Typography sx={{fontSize: '12px', color: '#A8A49E'}}>
        {
          row.status === 'COMPLETE' ?
            translate('case.Monitor.Expanded.NotMarked') :
            translate('case.Monitor.Expanded.MarksAfterComplete')
        }
      </Typography>
    );
  }

  const gradeText = row.markingGrade === 'FAIL' ?
    `${getGradeLabel(row.markingGrade)} · ${getFailureCategoryLabel(row, domainGuidesByRn)}` :
    getGradeLabel(row.markingGrade);
  const markedText = [
    formatMetricDate(row.markingMetricDate),
    row.markingMarkerType ? translate(`case.Monitor.DetailText.MarkedBy.${row.markingMarkerType}`) : '',
  ].filter(Boolean).join(' · ');

  return (
    <>
      <Typography sx={{fontSize: '12px', fontWeight: 600, color: gradeColours[row.markingGrade] || '#1A1916'}}>
        {gradeText}
      </Typography>
      {
        markedText &&
        <Typography sx={{fontSize: '11px', color: '#A8A49E', mt: 0.25}}>
          {markedText}
        </Typography>
      }
    </>
  );
};

MarkDetail.propTypes = {
  row: PropTypes.object.isRequired,
  domainGuidesByRn: PropTypes.object.isRequired,
};

const RowDetails = (props) => {
  const {
    row,
    now,
    domainGuidesByRn,
    participantDetails,
  } = props;
  const [detailState, setDetailState] = React.useState({status: 'LOADING', caseDetail: null, pendingJobs: []});

  React.useEffect(() => {
    let isActive = true;

    const loadDetails = async () => {
      try {
        const [caseDetail, jobsResponse] = await Promise.all([
          backend.case.getCase(row.caseRn),
          backend.agentJobs.getCaseJobs(row.caseRn),
        ]);

        if (!isActive) return;

        setDetailState({
          status: 'LOADED',
          caseDetail,
          pendingJobs: (jobsResponse?.jobs || []).filter((job) => job.status === 'PENDING'),
        });
      } catch {
        if (!isActive) return;

        setDetailState({status: 'FAILED', caseDetail: null, pendingJobs: []});
      }
    };

    loadDetails();

    return () => {
      isActive = false;
    };
  }, [row.caseRn]);

  const lastLogEntry = getLastLogEntry(detailState.caseDetail);
  const participantList = getParticipantList(row, participantDetails);

  return (
    <Box
      sx={{
        px: 3,
        py: 1.5,
        backgroundColor: '#FAFAFB',
        borderBottom: '1px solid #F1F1F4',
      }}
    >
      {
        detailState.status === 'LOADING' &&
        <Typography sx={{fontSize: '12px', color: '#A8A49E'}}>
          <Translate expression='case.Monitor.Expanded.Loading' />
        </Typography>
      }
      {
        detailState.status === 'FAILED' &&
        <Typography sx={{fontSize: '12px', color: '#C4512C'}}>
          <Translate expression='case.Monitor.Expanded.LoadFailed' />
        </Typography>
      }
      {
        detailState.status === 'LOADED' &&
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {xs: '1fr', md: '3fr 2fr 2fr 2fr auto'},
            columnGap: 3,
            rowGap: 2,
            alignItems: 'start',
          }}
        >
          <DetailSection
            label={
              lastLogEntry ?
                translate('case.Monitor.Expanded.LastLogEntryAt', {time: formatDateTime(lastLogEntry.createdAt)}) :
                translate('case.Monitor.Expanded.LastLogEntry')
            }
          >
            <Typography
              sx={{
                fontSize: '12px',
                color: lastLogEntry ? '#1A1916' : '#A8A49E',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {lastLogEntry ? truncateMessage(lastLogEntry.message) : translate('case.Monitor.Expanded.NoLogEntry')}
            </Typography>
          </DetailSection>
          <DetailSection label={translate('case.Monitor.Expanded.NextJob')}>
            <NextJobDetail job={getEarliestJob(detailState.pendingJobs)} now={now} />
          </DetailSection>
          <DetailSection label={translate('case.Monitor.Expanded.Mark')}>
            <MarkDetail row={row} domainGuidesByRn={domainGuidesByRn} />
          </DetailSection>
          <DetailSection label={translate('case.Monitor.Expanded.Participants')}>
            <Stack spacing={0.75}>
              {
                participantList.map((participant) => (
                  <Box key={participant.personRn} sx={{minWidth: 0}}>
                    <Typography sx={{fontSize: '12px', color: '#1A1916'}}>
                      <Box component='span' sx={{color: '#A8A49E', mr: 0.5}}>
                        {participant.kindLabel}
                      </Box>
                      {participant.name}
                    </Typography>
                    <Typography sx={{fontSize: '10px', fontFamily: 'monospace', color: '#A8A49E', wordBreak: 'break-all'}}>
                      {participant.personRn}
                    </Typography>
                  </Box>
                ))
              }
            </Stack>
          </DetailSection>
          <Button
            variant='contained'
            disableElevation
            endIcon={<ChevronRight />}
            onClick={() => window.open(`/case/${encodeURIComponent(row.caseRn)}`, '_blank', 'noopener,noreferrer')}
            sx={{
              'justifySelf': {xs: 'start', md: 'end'},
              'px': 1.5,
              'py': 0.5,
              'fontSize': '12px',
              'fontWeight': 600,
              'textTransform': 'none',
              'whiteSpace': 'nowrap',
              'borderRadius': '6px',
              'color': '#FFFFFF',
              'backgroundColor': '#D97757',
              '& .MuiButton-endIcon': {
                ml: 0.5,
              },
              '& .MuiButton-endIcon > *:nth-of-type(1)': {
                fontSize: '16px',
              },
              '&:hover': {
                backgroundColor: '#C4512C',
              },
            }}
          >
            <Translate expression='case.Monitor.Expanded.OpenCase' />
          </Button>
        </Box>
      }
    </Box>
  );
};

RowDetails.propTypes = {
  row: PropTypes.object.isRequired,
  now: PropTypes.number.isRequired,
  domainGuidesByRn: PropTypes.object.isRequired,
  participantDetails: PropTypes.object.isRequired,
};

const FlagGroupRow = (props) => {
  const {
    row,
    now,
    domainGuidesByRn,
    participantDetails,
  } = props;
  const [isExpanded, setIsExpanded] = React.useState(false);

  const detailLabel = getFlagDetailLabel(row, now, domainGuidesByRn);
  const otherFlags = row.flags.slice(1);
  const participantSummary = getParticipantSummary(row, participantDetails);
  const openForLabel = getOpenForLabel(row, now);

  return (
    <>
      <ButtonBase
        onClick={() => setIsExpanded((currentValue) => !currentValue)}
        sx={{
          'display': 'grid',
          'gridTemplateColumns': rowGridColumns,
          'justifyContent': 'start',
          'alignItems': 'center',
          'columnGap': 2,
          'width': '100%',
          'px': 3,
          'py': 1,
          'textAlign': 'left',
          'borderBottom': '1px solid #F1F1F4',
          'backgroundColor': isExpanded ? '#FAFAFB' : 'transparent',
          '&:hover': {
            backgroundColor: '#FAFAFB',
          },
        }}
      >
        <Typography sx={{fontSize: '12px', fontFamily: 'monospace', color: '#4B4B55', whiteSpace: 'nowrap'}}>
          {row.caseRn}
        </Typography>
        <Typography sx={{fontSize: '12px', fontWeight: 500, color: statusColours[row.status] || '#1A1916'}}>
          {getStatusLabel(row.status)}
        </Typography>
        <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap sx={{minWidth: 0}}>
          {
            detailLabel &&
            <DetailChip chipStyle={detailChipStyles[row.primaryFlag] || mutedChipStyle}>
              {detailLabel}
            </DetailChip>
          }
          {
            otherFlags.map((flagKey) => (
              <DetailChip key={flagKey} chipStyle={mutedChipStyle}>
                {translate('case.Monitor.DetailText.Also', {flag: lowerFirst(translate(`case.Monitor.Flags.${flagKey}.Title`))})}
              </DetailChip>
            ))
          }
          {
            row.nextJob &&
            <DetailChip chipStyle={mutedChipStyle}>
              {translate('case.Monitor.DetailText.NextJob', {time: formatDateTime(row.nextJob.executeAt)})}
            </DetailChip>
          }
        </Stack>
        <Stack direction='row' spacing={0.75} alignItems='center' flexWrap='wrap' useFlexGap sx={{minWidth: 0}}>
          {
            participantSummary.caregivers.length > 0 &&
            <Typography component='span' sx={{fontSize: '12px', color: '#1A1916'}}>
              {formatNameList(participantSummary.caregivers)}
            </Typography>
          }
          {
            participantSummary.caregivers.length > 0 && participantSummary.clients.length > 0 &&
            <Typography component='span' sx={{fontSize: '12px', color: '#D0CDC8'}}>
              ·
            </Typography>
          }
          {
            participantSummary.clients.length > 0 &&
            <Typography component='span' sx={{fontSize: '12px', color: '#6D7280'}}>
              {formatNameList(participantSummary.clients)}
            </Typography>
          }
          {
            participantSummary.others.map((participant) => (
              <Box
                key={participant.participantRn}
                component='span'
                title={participant.participantRn}
                sx={{
                  fontSize: '11px',
                  lineHeight: '16px',
                  px: 0.75,
                  border: '1px solid #ECECF1',
                  borderRadius: '4px',
                  color: '#A8A49E',
                  whiteSpace: 'nowrap',
                }}
              >
                {participant.label}
              </Box>
            ))
          }
        </Stack>
        <Typography sx={{fontSize: '12px', color: '#4B4B55', whiteSpace: 'nowrap'}}>
          {formatDateTime(row.createdAt)}
        </Typography>
        <Typography sx={{fontSize: '12px', color: '#4B4B55', whiteSpace: 'nowrap'}}>
          {getActivityLabel(row, now)}
        </Typography>
        <Typography sx={{fontSize: '12px', fontFamily: 'monospace', color: openForLabel ? '#4B4B55' : '#D0CDC8', textAlign: 'right'}}>
          {openForLabel || '-'}
        </Typography>
        {
          isExpanded ?
            <KeyboardArrowUp sx={{fontSize: 18, color: '#A8A49E'}} /> :
            <KeyboardArrowDown sx={{fontSize: 18, color: '#A8A49E'}} />
        }
      </ButtonBase>
      {
        isExpanded &&
        <RowDetails
          row={row}
          now={now}
          domainGuidesByRn={domainGuidesByRn}
          participantDetails={participantDetails}
        />
      }
    </>
  );
};

FlagGroupRow.propTypes = {
  row: PropTypes.object.isRequired,
  now: PropTypes.number.isRequired,
  domainGuidesByRn: PropTypes.object.isRequired,
  participantDetails: PropTypes.object.isRequired,
};

export default React.memo(FlagGroupRow);
